import { useEffect, useRef } from "react";
import { useStudyBuilderStore } from "@/stores/study-builder-store";
import type { CrfDefinition } from "@/api/protocol-types";

export interface FieldTagItem {
  fieldId: string;
  fieldLabel: string;
  crfId: string;
  crfName: string;
  domain: string;
  variable: string;
  /** Display string: "DOMAIN.VARIABLE" */
  tag: string;
}

interface FieldTagDropdownProps {
  filter: string;
  selectedIndex: number;
  onSelect: (field: FieldTagItem) => void;
  position: { top: number; left: number };
}

export function FieldTagDropdown({
  filter,
  selectedIndex,
  onSelect,
  position,
}: FieldTagDropdownProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const crfs = useStudyBuilderStore((s) => s.crfs);
  const allFields = flattenCrfFields(crfs);
  const filtered = filterFieldTags(allFields, filter);

  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[selectedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (filtered.length === 0) return null;

  return (
    <div
      ref={listRef}
      className="absolute z-50 max-h-40 w-64 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
      style={{ top: position.top, left: position.left }}
    >
      {filtered.map((field, i) => (
        <button
          key={`${field.crfId}-${field.fieldId}`}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(field);
          }}
          className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
            i === selectedIndex
              ? "bg-violet-500/10 text-violet-600 dark:text-violet-400"
              : "text-foreground hover:bg-muted"
          }`}
        >
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-violet-500/10 text-[8px] font-bold text-violet-600 dark:text-violet-400">
            #
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">{field.fieldLabel}</div>
            <div className="flex items-center gap-1 truncate text-[10px] text-muted-foreground">
              <span>{field.crfName}</span>
              <span className="text-violet-500">{field.tag}</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function flattenCrfFields(crfs: CrfDefinition[]): FieldTagItem[] {
  const items: FieldTagItem[] = [];
  for (const crf of crfs) {
    for (const section of crf.sections ?? []) {
      for (const field of section.fields ?? []) {
        const domain = field.cdashDomain || "";
        const variable = field.cdashVariable || field.name || "";
        items.push({
          fieldId: field.id,
          fieldLabel: field.label || field.name,
          crfId: crf.id,
          crfName: crf.name,
          domain,
          variable,
          tag: domain && variable ? `${domain}.${variable}` : variable || field.id,
        });
      }
    }
  }
  return items;
}

export function filterFieldTags(fields: FieldTagItem[], filter: string): FieldTagItem[] {
  const q = filter.toLowerCase();
  return fields.filter(
    (f) =>
      f.fieldLabel.toLowerCase().includes(q) ||
      f.tag.toLowerCase().includes(q) ||
      f.crfName.toLowerCase().includes(q),
  );
}

export function useFieldTags(): FieldTagItem[] {
  const crfs = useStudyBuilderStore((s) => s.crfs);
  return flattenCrfFields(crfs);
}
